import { queryOptions } from "@tanstack/react-query";
import { listThemeSettings } from "./theme.functions";

/**
 * The admin-editable theme system (migration 018). Each of the five
 * directions has ONE ThemeSettings JSON blob server-side; the public app
 * layers those values over the stylesheet defaults as CSS custom properties
 * (see buildThemeCss), so an unfetched or malformed theme still renders from
 * styles.css. All colors are #RRGGBB hex; radius is rem.
 */

export type ThemeId = "v1" | "v2" | "v3" | "v4" | "v5";

export type FontKey =
  | "inter"
  | "playfair-display"
  | "cormorant-garamond"
  | "lora"
  | "source-serif-4"
  | "libre-franklin"
  | "dm-sans"
  | "space-grotesk";

export type ThemeSettings = {
  version: 1;
  fonts: { display: FontKey; body: FontKey };
  topbar: { background: string; foreground: string };
  content: { background: string; foreground: string; muted: string; mutedForeground: string };
  highlight: {
    primary: string;
    primaryForeground: string;
    accent: string;
    accentForeground: string;
    brand: string;
    brandForeground: string;
    brand2: string;
    /** Second stop of --brand-gradient (v2/v5 end on navy, not brand2). */
    gradientEnd: string;
    ring: string;
    /**
     * Inline text-link colour, independent of primary (which paints buttons).
     * Optional: blobs saved before it existed fall back to the stylesheet's
     * per-theme default (see DEFAULT_LINK / styles.css --link).
     */
    link?: string;
  };
  surfaces: {
    card: string;
    cardForeground: string;
    secondary: string;
    secondaryForeground: string;
    border: string;
  };
  shape: { radius: number };
  /** Art-direction prefix for AI image generation: "<this> <specifics>". */
  imagePrompt: string;
};

export type ThemeRow = { theme_id: ThemeId; settings: ThemeSettings; updated_at: string };
export type ThemeSettingsResponse = { active: ThemeId; themes: ThemeRow[] };

/**
 * Every selectable font. `stack` is the full CSS font-family value (with the
 * same fallbacks styles.css ships); `family` is the Google Fonts URL param —
 * web/index.html's single fonts <link> must load every family listed here.
 */
export const FONT_OPTIONS: Record<FontKey, { label: string; stack: string; family: string }> = {
  inter: {
    label: "Inter",
    stack: '"Inter", ui-sans-serif, system-ui, sans-serif',
    family: "Inter:wght@400;500;600;700",
  },
  "playfair-display": {
    label: "Playfair Display",
    stack: '"Playfair Display", "Times New Roman", Georgia, serif',
    family: "Playfair+Display:wght@600;700",
  },
  "cormorant-garamond": {
    label: "Cormorant Garamond",
    stack: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
    family: "Cormorant+Garamond:wght@500;600;700",
  },
  lora: {
    label: "Lora",
    stack: '"Lora", Georgia, "Times New Roman", serif',
    family: "Lora:wght@400;500;600;700",
  },
  "source-serif-4": {
    label: "Source Serif",
    stack: '"Source Serif 4", Georgia, "Times New Roman", serif',
    family: "Source+Serif+4:wght@400;500;600;700",
  },
  "libre-franklin": {
    label: "Libre Franklin",
    stack: '"Libre Franklin", ui-sans-serif, system-ui, sans-serif',
    family: "Libre+Franklin:wght@400;500;600;700",
  },
  "dm-sans": {
    label: "DM Sans",
    stack: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
    family: "DM+Sans:wght@400;500;600;700",
  },
  "space-grotesk": {
    label: "Space Grotesk",
    stack: '"Space Grotesk", ui-sans-serif, system-ui, sans-serif',
    family: "Space+Grotesk:wght@400;500;600;700",
  },
};

/**
 * One theme's settings as CSS custom properties. Used two ways: inline on the
 * admin preview wrapper (vars cascade, nothing outside the panel changes) and
 * serialized into [data-direction] blocks by buildThemeCss for the live site.
 * --popover mirrors card, --input mirrors border, and --brand-gradient is
 * derived — those stay out of the stored schema on purpose.
 */
export function cssVarsForTheme(s: ThemeSettings): Record<string, string> {
  return {
    "--background": s.content.background,
    "--foreground": s.content.foreground,
    "--muted": s.content.muted,
    "--muted-foreground": s.content.mutedForeground,
    "--card": s.surfaces.card,
    "--card-foreground": s.surfaces.cardForeground,
    "--popover": s.surfaces.card,
    "--popover-foreground": s.surfaces.cardForeground,
    "--secondary": s.surfaces.secondary,
    "--secondary-foreground": s.surfaces.secondaryForeground,
    "--border": s.surfaces.border,
    "--input": s.surfaces.border,
    "--primary": s.highlight.primary,
    "--primary-foreground": s.highlight.primaryForeground,
    "--accent": s.highlight.accent,
    "--accent-foreground": s.highlight.accentForeground,
    "--ring": s.highlight.ring,
    // Omitted when unset so the [data-direction] stylesheet default applies
    // rather than collapsing links back onto the button colour.
    ...(s.highlight.link ? { "--link": s.highlight.link } : {}),
    "--brand": s.highlight.brand,
    "--brand-foreground": s.highlight.brandForeground,
    "--brand-2": s.highlight.brand2,
    "--brand-gradient": `linear-gradient(135deg, ${s.highlight.brand}, ${s.highlight.gradientEnd})`,
    "--topbar-background": s.topbar.background,
    "--topbar-foreground": s.topbar.foreground,
    "--radius": `${s.shape.radius}rem`,
    "--font-display": FONT_OPTIONS[s.fonts.display].stack,
    "--font-body": FONT_OPTIONS[s.fonts.body].stack,
  };
}

/**
 * Stylesheet default link colour per theme — the same values styles.css ships
 * in each [data-direction] block. The editor seeds a missing `link` from here
 * so the field shows what the site is actually rendering.
 */
export const DEFAULT_LINK: Record<ThemeId, string> = {
  v1: "#1a56db",
  v2: "#1a56db",
  v3: "#0f6b73",
  v4: "#8a5a25",
  v5: "#1a56db",
};

/** Fill in keys added after a blob was saved, so old rows edit cleanly. */
export function withThemeDefaults(themeId: ThemeId, s: ThemeSettings): ThemeSettings {
  if (s.highlight.link) return s;
  return { ...s, highlight: { ...s.highlight, link: DEFAULT_LINK[themeId] } };
}

/**
 * CSS text for ALL themes, injected as a <style> after the compiled
 * stylesheet — same selectors, later in document order, so the stored values
 * win without !important and ?v= previews of any theme get DB values too.
 */
export function buildThemeCss(themes: Array<Pick<ThemeRow, "theme_id" | "settings">>): string {
  return themes
    .map(({ theme_id, settings }) => {
      const vars = Object.entries(cssVarsForTheme(settings))
        .map(([k, v]) => `${k}:${v};`)
        .join("");
      return `[data-direction="${theme_id}"]{${vars}}`;
    })
    .join("\n");
}

/**
 * Same resilience story as the nav (nav.queries.ts): the DB scales to zero,
 * the first call after idle can take 15s+, and the theme must not pop in
 * late on repeat visits — cache the last successful response and render from
 * it immediately while revalidating.
 */
const THEME_CACHE_KEY = "mii-theme-cache-v1";

function readThemeCache(): ThemeSettingsResponse | undefined {
  if (typeof localStorage === "undefined") return undefined;
  try {
    const raw = localStorage.getItem(THEME_CACHE_KEY);
    return raw ? (JSON.parse(raw) as ThemeSettingsResponse) : undefined;
  } catch {
    return undefined;
  }
}

export const themeSettingsQueryOptions = () =>
  queryOptions({
    queryKey: ["theme-settings"],
    queryFn: async () => {
      const data = await listThemeSettings();
      try {
        localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(data));
      } catch {
        // storage full/blocked — cache is best-effort
      }
      return data;
    },
    placeholderData: readThemeCache(),
    staleTime: 5 * 60_000,
    retry: 8,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  });
